import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Inbox as InboxIcon, MessageCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/billing";
import { useInbox } from "@/lib/inbox";
import { useTeam } from "@/lib/team";
import { ago } from "@/pages/team/DeviceStatus";
import { CsvChip, CsvPicker } from "@/pages/team/CsvAttach";

function Message({ n, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState(null);
  if (n.from_assistant) {
    return (
      <li className="ml-auto w-fit max-w-[85%]" data-testid={`inbox-mine-${n.id}`}>
        <div className="rounded-2xl rounded-br-sm bg-[#0053DA] px-3 py-1.5 text-sm text-white">{n.text}<CsvChip csv={n.csv} dark /></div>
        <p className="mt-0.5 text-right text-[10px] text-slate-400">You · {ago(n.created_at)?.replace("active ", "")}</p>
      </li>
    );
  }
  const reply = async (t) => {
    const msg = (t || text).trim() || (csv ? `Sent you a spreadsheet: ${csv.name}` : "");
    if (!msg) return;
    setBusy(true);
    try { await api.post(`/inbox/${n.id}/reply`, { text: msg.slice(0, 300), csv_id: csv?.id }); setText(""); setCsv(null); toast.success("Reply sent"); onChanged(); }
    catch (e) { toast.error(errorMessage(e, "Couldn't send reply")); }
    finally { setBusy(false); }
  };
  return (
    <li className={`rounded-xl border p-3 ${n.read_at ? "border-slate-200 bg-white" : "border-[#0053DA]/40 bg-blue-50"}`} data-testid={`inbox-msg-${n.id}`}>
      <p className="text-sm text-slate-900" data-testid={`inbox-text-${n.id}`}>{n.text}</p>
      <CsvChip csv={n.csv} />
      <p className="mt-0.5 text-[10px] text-slate-400">{ago(n.created_at)?.replace("active ", "")}</p>
      {n.reply ? (
        <p className="mt-2 ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-[#0053DA] px-3 py-1.5 text-sm text-white" data-testid={`inbox-reply-${n.id}`}>{n.reply}<CsvChip csv={n.reply_csv} dark /></p>
      ) : (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {["Got it", "On it!", "Can't today"].map((q) => (
              <button key={q} type="button" onClick={() => reply(q)} disabled={busy} data-testid={`inbox-quick-${n.id}-${q.replace(/\W+/g, "-").toLowerCase()}`}
                className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-700 transition-colors hover:border-[#0053DA] hover:text-[#0053DA] disabled:opacity-50">{q}</button>
            ))}
          </div>
          <CsvPicker value={csv} onChange={setCsv} />
          <form onSubmit={(e) => { e.preventDefault(); reply(); }} className="flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} placeholder="Reply…" data-testid={`inbox-reply-input-${n.id}`}
              className="h-10 min-w-0 flex-1 rounded-full border border-slate-300 px-3 text-sm focus:border-[#0053DA] focus:outline-none" />
            <button type="submit" disabled={busy || (!text.trim() && !csv)} aria-label="Send reply" data-testid={`inbox-reply-send-${n.id}`}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0053DA] text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </form>
        </div>
      )}
    </li>
  );
}

// Assistant -> owner: a message (and optional spreadsheet) from the inbox.
function ToOwner({ onSent }) {
  const [text, setText] = useState("");
  const [csv, setCsv] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const t = text.trim() || (csv ? `Sent you a spreadsheet: ${csv.name}` : "");
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inbox/to-owner", { text: t.slice(0, 300), csv_id: csv?.id });
      toast.success(data.phones ? "Sent to the owner's phone" : "Sent to the owner");
      setText(""); setCsv(null); onSent();
    } catch (e) { toast.error(errorMessage(e, "Couldn't send")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="inbox-to-owner">
      <p className="text-xs font-semibold text-slate-700">Message the owner</p>
      <CsvPicker value={csv} onChange={setCsv} />
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} placeholder="Type a message…" data-testid="inbox-to-owner-input"
          className="h-10 min-w-0 flex-1 rounded-full border border-slate-300 px-3 text-base focus:border-[#0053DA] focus:outline-none sm:text-sm" />
        <button type="submit" disabled={busy || (!text.trim() && !csv)} aria-label="Send to owner" data-testid="inbox-to-owner-send"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0053DA] text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

export default function InboxPage() {
  const { items, reload } = useInbox();
  useEffect(() => {
    const unread = items.filter((n) => !n.read_at && !n.from_assistant);
    if (unread.length) Promise.all(unread.map((n) => api.post(`/inbox/${n.id}/read`).catch(() => {}))).then(reload);
  }, [items, reload]);
  return (
    <div className="mx-auto max-w-lg space-y-4" data-testid="inbox-page">
      <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight text-slate-900"><InboxIcon className="h-7 w-7 text-[#0053DA]" /> Messages</h1>
      <p className="text-sm text-slate-600">Messages with the account owner. Tap a quick reply, type your own, or attach a spreadsheet.</p>
      <ToOwner onSent={reload} />
      {items.length ? <ul className="space-y-3">{items.map((n) => <Message key={n.id} n={n} onChanged={reload} />)}</ul>
        : <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="inbox-empty">No messages yet.</p>}
    </div>
  );
}

export function InboxHeaderButton() {
  const { unread } = useInbox();
  return (
    <Link to="/inbox" data-testid="open-inbox" aria-label={`Messages${unread ? `, ${unread} unread` : ""}`} title="Messages"
      className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50">
      <MessageCircle className="h-4 w-4" />
      {unread > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white" data-testid="inbox-unread-count">{unread}</span>}
    </Link>
  );
}

// Banner on every screen while a nudge is unread (assistants only).
export function InboxBanner() {
  const { isAssistant } = useTeam();
  const { items } = useInbox();
  const { pathname } = useLocation();
  const n = items.find((x) => !x.read_at && !x.from_assistant);
  if (!isAssistant || !n || pathname === "/inbox") return null;
  return (
    <Link to="/inbox" className="mb-4 flex items-center gap-3 rounded-xl border border-[#0053DA]/40 bg-blue-50 p-3 text-sm transition-colors hover:bg-blue-100" data-testid="inbox-banner">
      <MessageCircle className="h-5 w-5 shrink-0 text-[#0053DA]" />
      <span className="min-w-0 flex-1"><b className="text-slate-900">New message:</b> <span className="text-slate-700" data-testid="inbox-banner-text">{n.text}</span></span>
      <span className="shrink-0 text-xs font-semibold text-[#0053DA]">Reply</span>
    </Link>
  );
}
